import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Auto-save status pill rendered in the panel header (86d32t0xr).
 *
 * Renders one of three states using existing store data:
 *   - <b>Saving…</b>   while `isSaving === true`.
 *   - <b>Saved Xs ago</b> when `saveError === null` and a `lastSavedAt` exists.
 *     The "Xs ago" string ticks every 10s so sales sees freshness drifting.
 *   - <b>Save failed (Retry)</b> when `saveError !== null` — single-click retry
 *     button invokes the same handler that runs on manual Save, surfacing the
 *     full toast (the auto-save itself is silent on errors per the ticket).
 *
 * Empty when there's nothing meaningful to show (no save has happened yet on
 * a clean draft).
 */
interface AutoSaveStatusChipProps {
  isSaving: boolean;
  lastSavedAt: string | null;
  saveError: string | null;
  /** Invoked when sales clicks the Retry button on a failed save. */
  onRetry: () => void;
  /** Disable the retry button while another long-running action (submit) holds the panel. */
  disabled?: boolean;
}

export function AutoSaveStatusChip({
  isSaving,
  lastSavedAt,
  saveError,
  onRetry,
  disabled,
}: AutoSaveStatusChipProps) {
  // Saving and Saved-N-seconds-ago both need a render trigger as time elapses.
  // useNowEverySecond keeps the relative timestamp fresh without coupling the
  // store to a timer.
  const now = useNowEvery(10_000);

  if (isSaving) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" />
        Saving…
      </span>
    );
  }

  if (saveError) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-destructive/10 px-2 py-0.5 text-xs text-destructive">
        <AlertTriangle className="h-3 w-3" />
        Save failed
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRetry}
          disabled={disabled}
          className="h-5 px-1.5 text-xs text-destructive hover:bg-destructive/15"
          title={saveError}
        >
          Retry
        </Button>
      </span>
    );
  }

  if (!lastSavedAt) return null;

  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 rounded-full bg-success/10 px-2 py-0.5 text-xs text-success',
      )}
      title={new Date(lastSavedAt).toLocaleString()}
    >
      <Check className="h-3 w-3" />
      Saved {relativeAge(lastSavedAt, now)}
    </span>
  );
}

/**
 * Returns `now` and re-renders the calling component every `intervalMs`.
 * 10s is enough granularity for "Xs ago" / "Xm ago" without causing visible
 * jitter; the user only ever sees the second-aging tick on the first 60s.
 */
function useNowEvery(intervalMs: number): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
}

function relativeAge(isoTimestamp: string, nowMs: number): string {
  const savedMs = new Date(isoTimestamp).getTime();
  if (Number.isNaN(savedMs)) return 'just now';
  const deltaSeconds = Math.max(0, Math.floor((nowMs - savedMs) / 1000));
  if (deltaSeconds < 5) return 'just now';
  if (deltaSeconds < 60) return `${deltaSeconds}s ago`;
  const minutes = Math.floor(deltaSeconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
